"use client";

import { c as _c } from "react/compiler-runtime";
import Image, { ImageProps } from "next/image";
import React, { useEffect, useState, useCallback, memo } from "react";
import { cloudflareImageLoader } from "@/cloudflareImageLoader";
interface ImageWithFallbackProps extends ImageProps {
  fallback?: ImageProps["src"];
  /**
   * Enable smooth fade-in animation when image loads (default: true).
   * LCP: the fade holds the image at opacity:0 until JS hydrates+loads — so for an
   * above-fold/LCP image pass fadeIn={false} alongside `priority`. `priority` ALONE
   * is WORSE (preloads bytes the fade still hides). Measured here: 14.9s → 3.6s.
   */
  fadeIn?: boolean;
  /** Custom placeholder to render while loading or on error (before fallback) */
  renderPlaceholder?: () => React.ReactNode;
}
export const fallbackImage = "/img/placeholder/instagram-post-placeholder.jpeg";

// Helper to check if src is a valid, non-empty value
const isValidSrc = (src: ImageProps["src"] | undefined | null): src is ImageProps["src"] => {
  if (!src) return false;
  if (typeof src === "string" && src.trim() === "") return false;
  return true;
};
const ImageWithFallback = t0 => {
  const $ = _c(35);
  let alt;
  let className;
  let props;
  let renderPlaceholder;
  let src;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      fallback: t1,
      alt,
      src,
      fadeIn: t2,
      className,
      renderPlaceholder,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = className;
    $[3] = props;
    $[4] = renderPlaceholder;
    $[5] = src;
    $[6] = t1;
    $[7] = t2;
  } else {
    alt = $[1];
    className = $[2];
    props = $[3];
    renderPlaceholder = $[4];
    src = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const fallback = t1 === undefined ? fallbackImage : t1;
  const fadeIn = t2 === undefined ? true : t2;
  const validSrc = isValidSrc(src) ? src : null;
  const [status, setStatus] = useState(validSrc ? "loading" : "error");
  let t3;
  let t4;
  if ($[8] !== src) {
    t3 = () => {
      setStatus(isValidSrc(src) ? "loading" : "error");
    };
    t4 = [src];
    $[8] = src;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      requestAnimationFrame(() => {
        setStatus("loaded");
      });
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleLoad = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e => {
      setStatus("error");
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleError = t6;
  status === "loading" || status === "error" && renderPlaceholder && !validSrc;
  const imageSrc = status === "error" || !validSrc ? fallback : validSrc;
  const t7 = `relative overflow-hidden ${className}`;
  let t8;
  if ($[13] !== props.height || $[14] !== props.style || $[15] !== props.width) {
    t8 = {
      width: props.width,
      height: props.height,
      ...props.style
    };
    $[13] = props.height;
    $[14] = props.style;
    $[15] = props.width;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== renderPlaceholder || $[18] !== status) {
    t9 = (status === "loading" || status === "error" && renderPlaceholder) && renderPlaceholder && <div className="absolute inset-0 z-0">{renderPlaceholder()}</div>;
    $[17] = renderPlaceholder;
    $[18] = status;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = props.unoptimized ? undefined : cloudflareImageLoader;
  const t11 = `transition-opacity duration-500 ease-out ${className}`;
  const t12 = fadeIn && status === "loading" ? 0 : status === "error" && renderPlaceholder ? 0 : 1;
  let t13;
  if ($[20] !== t12) {
    t13 = {
      opacity: t12,
      objectFit: "cover"
    };
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = !props.width && !props.height;
  let t15;
  if ($[22] !== alt || $[23] !== imageSrc || $[24] !== props || $[25] !== t10 || $[26] !== t11 || $[27] !== t13 || $[28] !== t14) {
    t15 = <Image loader={t10} alt={alt} onError={handleError} onLoad={handleLoad} src={imageSrc} className={t11} style={t13} fill={t14} width={props.width} height={props.height} {...props} />;
    $[22] = alt;
    $[23] = imageSrc;
    $[24] = props;
    $[25] = t10;
    $[26] = t11;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t16 = <div className={t7} style={t8}>{t9}{t15}</div>;
    $[30] = t15;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
export default memo(ImageWithFallback);